/**
 * <gd-slider-product>
 *
 * Product card used inside the "GD 3D slider" section. Handles color swatch
 * selection, size selection, price/image updates and quick add to cart.
 *
 * Expected children (all optional except the JSON payload):
 *   [data-gd-product-data]   <script type="application/json"> variant payload
 *   [data-gd-color]          color swatch buttons  (data-color)
 *   [data-gd-size]           size buttons           (data-size)
 *   .gd-slider-product__image--variant  layer this paints the selected
 *                            variant's photo onto (the primary and override
 *                            layers are owned by CSS and never mutated here)
 *   [data-gd-variant-id]     hidden `id` input inside the product form
 *   [data-gd-submit]         the add-to-cart submit button
 *   [data-gd-price]          price container
 */
if (!customElements.get('gd-slider-product')) {
  customElements.define(
    'gd-slider-product',
    class GDSliderProduct extends HTMLElement {
      connectedCallback() {
        const dataEl = this.querySelector('[data-gd-product-data]');
        if (!dataEl) return;

        try {
          this.data = JSON.parse(dataEl.textContent);
        } catch (error) {
          console.error('gd-slider-product: invalid JSON payload', error);
          return;
        }

        this.variantImage = this.querySelector('.gd-slider-product__image--variant');
        this.colorButtons = Array.from(this.querySelectorAll('[data-gd-color]'));
        this.sizeButtons = Array.from(this.querySelectorAll('[data-gd-size]'));
        this.variantInput = this.querySelector('[data-gd-variant-id]');
        this.submitButton = this.querySelector('[data-gd-submit]');
        this.submitButtonText = this.submitButton?.querySelector('[data-gd-submit-text]');
        this.priceEl = this.querySelector('[data-gd-price]');

        this.swapImageOnColorSelect = this.dataset.swapImageOnColorSelect === 'true';
        this.colorWasPicked = false;

        this.selectedColor = this.initialColor();
        this.selectedSize = this.initialSize();

        this.colorButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            // Materialize's carousel listens for taps on the slide — keep the
            // click from advancing the carousel.
            event.preventDefault();
            event.stopPropagation();
            this.selectColor(button.dataset.color, { userInitiated: true });
          });
        });

        this.sizeButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (button.disabled) return;
            this.selectSize(button.dataset.size);
          });
        });

        this.render({ updateImage: false });
      }

      initialColor() {
        const preselected = this.colorButtons.find((button) => button.classList.contains('is-selected'));
        if (preselected) return preselected.dataset.color;
        if (this.colorButtons.length) return this.colorButtons[0].dataset.color;

        const { colorOptionIndex, variants } = this.data;
        if (colorOptionIndex == null || !variants.length) return null;
        return variants[0].options[colorOptionIndex] ?? null;
      }

      initialSize() {
        const preselected = this.sizeButtons.find((button) => button.classList.contains('is-selected'));
        if (preselected) return preselected.dataset.size;
        return null;
      }

      selectColor(color, { userInitiated = false } = {}) {
        this.selectedColor = color;
        // Once a shopper picks a colour, the variant photo is more useful than
        // the section's static override art.
        if (userInitiated) this.colorWasPicked = true;
        this.render({ updateImage: true });
      }

      selectSize(size) {
        this.selectedSize = size;
        this.render({ updateImage: false });
      }

      /** Variants matching the currently selected colour (all, if no colour option). */
      matchingVariants() {
        const { colorOptionIndex } = this.data;
        if (colorOptionIndex == null || this.selectedColor == null) return this.data.variants;
        return this.data.variants.filter((variant) => variant.options[colorOptionIndex] === this.selectedColor);
      }

      currentVariant() {
        const { sizeOptionIndex, hasSizeOption } = this.data;
        const matching = this.matchingVariants();
        if (!matching.length) return null;

        if (hasSizeOption && sizeOptionIndex != null && this.selectedSize != null) {
          const exact = matching.find((variant) => variant.options[sizeOptionIndex] === this.selectedSize);
          if (exact) return exact;
        }

        return matching.find((variant) => variant.available) || matching[0];
      }

      render({ updateImage = true } = {}) {
        const { sizeOptionIndex, hasSizeOption } = this.data;
        const matching = this.matchingVariants();

        this.colorButtons.forEach((button) => {
          const isSelected = button.dataset.color === this.selectedColor;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        if (hasSizeOption && sizeOptionIndex != null) {
          let selectedStillValid = false;

          this.sizeButtons.forEach((button) => {
            const variant = matching.find((item) => item.options[sizeOptionIndex] === button.dataset.size);
            const available = Boolean(variant && variant.available);

            button.disabled = !available;
            button.classList.toggle('is-unavailable', !available);
            if (variant) {
              button.dataset.variantId = String(variant.id);
            } else {
              delete button.dataset.variantId;
            }

            const isSelected = available && button.dataset.size === this.selectedSize;
            if (isSelected) selectedStillValid = true;
            button.classList.toggle('is-selected', isSelected);
            button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          });

          // The previously chosen size may not exist in the new colour.
          if (this.selectedSize != null && !selectedStillValid) {
            this.selectedSize = null;
            this.sizeButtons.forEach((button) => {
              button.classList.remove('is-selected');
              button.setAttribute('aria-pressed', 'false');
            });
          }
        }

        const variant = this.currentVariant();
        this.updateBuyState(variant, hasSizeOption);
        if (variant) {
          this.updatePrice(variant);
          if (updateImage) this.updateImage(variant);
        }
      }

      updateBuyState(variant, hasSizeOption) {
        if (!this.submitButton) return;

        const needsSize = hasSizeOption && this.selectedSize == null;
        const purchasable = Boolean(variant && variant.available) && !needsSize;

        if (this.variantInput && variant) {
          this.variantInput.value = variant.id;
          this.variantInput.disabled = !purchasable;
        }

        this.submitButton.disabled = !purchasable;

        if (!this.submitButtonText) return;
        const strings = window.variantStrings || {};
        if (variant && !variant.available) {
          this.submitButtonText.textContent = strings.soldOut || 'Sold out';
        } else if (needsSize) {
          this.submitButtonText.textContent = this.dataset.chooseSizeLabel || strings.addToCart || 'Add to cart';
        } else {
          this.submitButtonText.textContent = strings.addToCart || 'Add to cart';
        }
      }

      updatePrice(variant) {
        if (!this.priceEl) return;
        const regular = this.priceEl.querySelector('[data-gd-price-regular]');
        const compare = this.priceEl.querySelector('[data-gd-price-compare]');

        if (regular) regular.textContent = variant.price;
        if (compare) {
          compare.textContent = variant.compareAtPrice || '';
          compare.hidden = !variant.compareAtPrice;
        }
      }

      /**
       * Paints the variant photo onto its own layer and toggles it on. The
       * primary <img> is deliberately never touched, so an inactive slide
       * always falls back to the product's default image rather than keeping
       * whatever colour was last selected.
       */
      updateImage(variant) {
        if (!this.variantImage) return;

        const show = this.swapImageOnColorSelect && this.colorWasPicked && Boolean(variant.featuredImage);
        if (show) {
          if (variant.srcset) this.variantImage.srcset = variant.srcset;
          this.variantImage.src = variant.featuredImage;
        }

        this.classList.toggle('is-variant-image-active', show);
      }
    }
  );
}
